import { noop } from '@utils/noop';
import { Dispatch, createContext, useContext } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { ObjectSchema, date, number, object, string } from 'yup';

export type CreateRaindropFormState = {
  name: string;
  providerId: string;
  casinoId: string | null;
  startDate: Date | null;
  endDate: Date | null;
  distributionFrom: number | null;
  distributionTo: number | null;
  freebetsPerRain: number | null;
  rainsPerDay: number | null;
  freebetValue: number | null;
  currency: string;
  claimLimitNumber: number | null;
  claimLimitPeriod: string | null;
  minMultiplier: number | null;
  freebetValidityDate: Date | null;
  claimDeadlineMinutes: number | null;
  playerMinBalance: number | null;
  playerMinWager: number | null;
  wagerPeriod: number | null;
  dayBudget: number | null;
  totalBudget: number | null;
};

export const createRaindropFormStateDefault: CreateRaindropFormState = {
  name: '',
  providerId: '',
  casinoId: null,
  startDate: null,
  endDate: null,
  distributionFrom: null,
  distributionTo: null,
  freebetsPerRain: null,
  rainsPerDay: null,
  freebetValue: null,
  currency: '',
  claimLimitNumber: null,
  claimLimitPeriod: null,
  minMultiplier: null,
  freebetValidityDate: null,
  claimDeadlineMinutes: null,
  playerMinBalance: null,
  playerMinWager: null,
  wagerPeriod: null,
  dayBudget: 0,
  totalBudget: 0,
};

export const createRaindropSchemaDefault: ObjectSchema<Partial<CreateRaindropFormState>> = object<
  Partial<CreateRaindropFormState>
>().shape({
  name: string().required('name is required'),
  providerId: string().required('operator is required'),
  casinoId: string().nullable().notRequired(),
  startDate: date()
    .required('startDate is required')
    .min(new Date(), 'Start Date must be greater than today')
    .nullable()
    .typeError('startDate must be a valid date'),
  endDate: date()
    .required('endDate is required')
    .nullable()
    .typeError('endDate must be a valid date')
    .when('startDate', (startDate, schema) => {
      if (startDate && startDate[0]) {
        return schema.min(startDate[0], 'End Date must be greater than or equal to Start Date');
      }
      return schema;
    }),
  distributionFrom: number()
    .required('distributionTimeFrom is required')
    .integer('distributionTimeFrom must be an integer')
    .nullable()
    .typeError('distributionTimeFrom must be a number'),
  distributionTo: number()
    .required('distributionTimeTo is required')
    .integer('distributionTimeTo must be an integer')
    .nullable()
    .typeError('distributionTimeTo must be a number'),
  freebetsPerRain: number()
    .required('numberOfFreebetsPerRain is required')
    .integer('numberOfFreebetsPerRain must be an integer')
    .positive('freebetsPerRain must be positive')
    .nullable()
    .typeError('freebetsPerRain must be a number'),
  rainsPerDay: number()
    .required('rains Per Day is required')
    .integer('rainCountsPerDay must be an integer')
    .positive('rainCountsPerDay must be positive')
    .nullable()
    .typeError('rainsPerDay must be a number'),
  freebetValue: number()
    .required('freebetValue is required')
    .positive('freebetValue must be positive')
    .nullable()
    .typeError('freebetValue must be a number'),
  currency: string().required('currency is required'),
  claimLimitNumber: number()
    .nullable()
    .notRequired()
    .integer('claimLimitNumber must be an integer')
    .typeError('claimLimitNumber must be a number'),
  claimLimitPeriod: string()
    .nullable()
    .notRequired()
    .typeError('claimLimitPeriod must be a string'),
  minMultiplier: number()
    .required('minMultiplier is required')
    .positive('minMultiplier must be positive')
    .nullable()
    .typeError('minMultiplier must be a number'),
  freebetValidityDate: date()
    .nullable()
    .notRequired()
    .typeError('freebetValidityDate must be a valid date'),
  claimDeadlineMinutes: number()
    .required('claimDeadline is required')
    .integer('claimDeadline must be an integer')
    .nullable()
    .typeError('claimDeadline must be a number'),
  playerMinBalance: number()
    .nullable()
    .notRequired()
    .typeError('playerMinBalance must be a number'),
  playerMinWager: number().nullable().notRequired().typeError('playerMinWager must be a number'),
  wagerPeriod: number().nullable().notRequired().typeError('wagerPeriod must be a number'),
  dayBudget: number().required('dayBudget is required').typeError('dayBudget must be a number'),
  totalBudget: number()
    .required('totalBudget is required')
    .typeError('totalBudget must be a number'),
});

type CreateRaindropFormContextType = {
  schema: ObjectSchema<Partial<CreateRaindropFormState>>;
  setSchema: Dispatch<ObjectSchema<Partial<CreateRaindropFormState>>>;
  methods?: UseFormReturn<CreateRaindropFormState>;
};

const createRaindropFormContextDefault = {
  schema: createRaindropSchemaDefault,
  setSchema: noop,
} satisfies CreateRaindropFormContextType;

export const CreateRaindropFormContext = createContext<CreateRaindropFormContextType>(
  createRaindropFormContextDefault
);

export const useCreateRaindropForm = () => {
  const { methods } = useContext(CreateRaindropFormContext);
  if (!methods) {
    throw new Error('CreateRaindropFormContext is empty');
  }

  return methods;
};

export const useManageCreateRaindropSchema = () => {
  const { setSchema } = useContext(CreateRaindropFormContext);

  return { setSchema };
};
